// Business-hours domain for Presence Control Center → Location settings tab (EIP-2017), plus the
// after-hours auto sign-out interval bounds (the interval applied to signals *outside* these
// windows — see the Sign-out tab). Extracted from the account-settings controller to keep that file
// focused on tracked state and orchestration; everything here is pure (no Ember services / tracked
// glue) so it's unit-testable on its own.
import { tracked } from '@glimmer/tracking';
import SimpleTime from 'garaje/utils/simple-time';

// Uppercase weekday keys (match java.time.DayOfWeek.name / the presence-service contract), ordered
// Monday→Sunday for display.
export const WEEKDAYS = [
  { key: 'MONDAY', label: 'Monday' },
  { key: 'TUESDAY', label: 'Tuesday' },
  { key: 'WEDNESDAY', label: 'Wednesday' },
  { key: 'THURSDAY', label: 'Thursday' },
  { key: 'FRIDAY', label: 'Friday' },
  { key: 'SATURDAY', label: 'Saturday' },
  { key: 'SUNDAY', label: 'Sunday' },
] as const;

// After-hours auto sign-out interval default (minutes). Matches the server default. Lives with
// business hours because the interval only applies once a business-hours window is configured.
export const AFTER_HOURS_ABSENCE_MINUTES_DEFAULT = 60;

/** API-facing per-day window: closed days carry no times. */
export interface DayWindowPayload {
  enabled: boolean;
  start?: string;
  end?: string;
}

/** The full business-hours block in API shape (held by the persisted snapshot and the draft getter). */
export interface BusinessHoursPayload {
  enabled: boolean;
  // After-hours presence report opt-in. Lives in the business-hours block (sibling of `enabled`);
  // forced false whenever business hours is disabled so we never persist an opt-in that can't fire.
  'after-hours-report-enabled': boolean;
  days: Record<string, DayWindowPayload>;
}

/**
 * Draft state for one weekday's business hours. Start/end are held in the TimePicker's 12-hour
 * format and converted to/from "HH:mm" for the API. Tracked so per-row edits drive the Save gating.
 */
export class BusinessHoursDayDraft {
  readonly key: string;
  readonly label: string;
  @tracked enabled: boolean;
  @tracked start: string | null;
  @tracked end: string | null;

  constructor(key: string, label: string, enabled: boolean, start: string | null, end: string | null) {
    this.key = key;
    this.label = label;
    this.enabled = enabled;
    this.start = start;
    this.end = end;
  }

  // Both times set and start strictly before end.
  get rangeValid(): boolean {
    if (!this.start || !this.end) return false;
    return SimpleTime.parse12Hour(this.start).asMinutes() < SimpleTime.parse12Hour(this.end).asMinutes();
  }

  // Both set but out of order — drives the inline error (distinct from not-yet-filled-in).
  get outOfOrder(): boolean {
    if (!this.start || !this.end) return false;
    return SimpleTime.parse12Hour(this.start).asMinutes() >= SimpleTime.parse12Hour(this.end).asMinutes();
  }

  // Closed days render disabled pickers; only an *open* day with an out-of-order range is invalid
  // (drives the red input border).
  get disabled(): boolean {
    return !this.enabled;
  }

  get invalid(): boolean {
    return this.enabled && this.outOfOrder;
  }

  // A saveable state: closed (no window) or open with a complete, ordered window.
  get valid(): boolean {
    return !this.enabled || this.rangeValid;
  }

  // The window in API shape; only valid to read when `valid` (enabled days must have times).
  get apiWindow(): DayWindowPayload {
    if (!this.enabled) return { enabled: false };
    return {
      enabled: true,
      start: SimpleTime.parse12Hour(this.start!).toString(),
      end: SimpleTime.parse12Hour(this.end!).toString(),
    };
  }
}

// Build the 7 per-day drafts from an API business-hours block (or a fresh/closed set when absent).
// Always returns all weekdays in display order so the form is complete.
export function buildBusinessHoursDays(
  businessHours?: { days?: Record<string, DayWindowPayload> } | null,
): BusinessHoursDayDraft[] {
  return WEEKDAYS.map(({ key, label }) => {
    const window = businessHours?.days?.[key];
    if (window?.enabled && window.start && window.end) {
      return new BusinessHoursDayDraft(
        key,
        label,
        true,
        formatTimeForPicker(SimpleTime.parse24Hour(window.start)),
        formatTimeForPicker(SimpleTime.parse24Hour(window.end)),
      );
    }
    return new BusinessHoursDayDraft(key, label, false, null, null);
  });
}

// Convert a SimpleTime (24-hour) into the 12-hour string the TimePicker expects.
export function formatTimeForPicker(time: SimpleTime): string {
  let displayHour = time.hour;
  const amPm = displayHour < 12 ? 'am' : 'pm';
  if (displayHour > 12) {
    displayHour -= 12;
  } else if (displayHour === 0) {
    displayHour = 12;
  }
  return `${displayHour}:${time.minute.toString().padStart(2, '0')} ${amPm}`;
}

// Normalize a business-hours payload to a stable, order-independent representation for the dirty
// diff: every weekday in fixed order, with missing/closed days collapsed to `{ enabled: false }`.
export function canonicalizeBusinessHours(payload: BusinessHoursPayload): {
  enabled: boolean;
  'after-hours-report-enabled': boolean;
  days: Array<[string, DayWindowPayload]>;
} {
  const days = WEEKDAYS.map(({ key }): [string, DayWindowPayload] => {
    const window = payload.days[key];
    if (!window || !window.enabled) return [key, { enabled: false }];
    return [key, { enabled: true, start: window.start, end: window.end }];
  });
  return {
    enabled: payload.enabled,
    'after-hours-report-enabled': payload['after-hours-report-enabled'],
    days,
  };
}
